import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box, Grid, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Cached, Download } from '@mui/icons-material';
import dayjs from 'dayjs';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import SoftSelect from 'components/SoftSelect';
import DialogConfirmation from 'shared/dialogs/DialogConfirmation';
import CancellationSummaryDialog from '../../policyCancellation/components/CancellationSummaryDialog';
import { reportError } from 'shared/services/raygunService';
import useCancelPolicy from '../../policyCancellation/helpers/useCancelPolicy';
import useCancellationRequest from '../../policyCancellation/helpers/useCancellationRequest';
import useFetchPolicyRefund from '../../policyCancellation/helpers/useFetchPolicyRefund';
import RefundEligibilitySelect from '../../policyCancellation/components/RefundEligibilitySelect';
import { policyCancellationReasons } from '../../policyCancellation/const/policyCancellationReasons';
import { policyCancellationMethodReceived } from '../../policyCancellation/const/policyCancellationMethodReceived';
import {
  policyCancellationDelayReasons,
  PolicyCancellationDelayReasonValue,
} from '../../policyCancellation/const/policyCancellationDelayReasons';
import { policyCancellationRefundEligibility } from '../../policyCancellation/const/policyCancellationRefundEligibility';
import { formatFileSize } from '../../policyCancellation/const/cancellationAttachments';

const REQUEST_STATUS = {
  PENDING: 'PENDING_CANCELLATION',
  APPROVED: 'APPROVED',
  DISMISSED: 'DISMISSED',
};

const path = 'POLICY_DETAILS.CANCELLATION_MANAGEMENT';

const findOption = (options, value) =>
  options.find((option) => option.value === value) ?? null;

const CancellationManagementTab = ({ policyDetails }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const policyId = policyDetails?.policy?.policyNumber;
  const grossPremium = policyDetails?.policy?.price?.grossPremium;

  const {
    cancellationRequest: latestRequest,
    fetchCancellationRequest,
    dismissPendingCancellation,
    downloadAttachment,
  } = useCancellationRequest();
  // A dismissed request is history only: the tab behaves as if there were no request.
  const cancellationRequest =
    latestRequest?.requestStatus === REQUEST_STATUS.DISMISSED
      ? null
      : latestRequest;
  const { cancelPolicy } = useCancelPolicy();
  const { refundDate, fetchRefundData } = useFetchPolicyRefund();

  const [cancellationRequestDate, setCancellationRequestDate] = useState(null);
  const [methodReceived, setMethodReceived] = useState(null);
  const [delayReason, setDelayReason] = useState(null);
  const [delayReasonOtherText, setDelayReasonOtherText] = useState('');
  const [reason, setReason] = useState(null);
  const [refundEligibility, setRefundEligibility] = useState(null);
  const [refundAmount, setRefundAmount] = useState('0');
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showConfirmDismiss, setShowConfirmDismiss] = useState(false);

  useEffect(() => {
    if (policyId) {
      fetchCancellationRequest(policyId);
    }
  }, [policyId, fetchCancellationRequest]);

  useEffect(() => {
    if (!cancellationRequest) {
      return;
    }
    setCancellationRequestDate(
      cancellationRequest.requestDate
        ? dayjs(cancellationRequest.requestDate)
        : null,
    );
    setMethodReceived(
      findOption(
        policyCancellationMethodReceived,
        cancellationRequest.methodReceived,
      ),
    );
    setDelayReason(
      findOption(
        policyCancellationDelayReasons,
        cancellationRequest.delayReason,
      ),
    );
    setDelayReasonOtherText(cancellationRequest.delayReasonOtherText ?? '');
    setReason(
      findOption(policyCancellationReasons, cancellationRequest.reason),
    );
    setRefundEligibility(
      findOption(
        policyCancellationRefundEligibility,
        cancellationRequest.refundEligibility,
      ),
    );
    setRefundAmount(
      cancellationRequest.amount?.amount != null
        ? `${cancellationRequest.amount.amount}`
        : '0',
    );
  }, [cancellationRequest]);

  const requestStatus = cancellationRequest?.requestStatus;
  const isPending = requestStatus === REQUEST_STATUS.PENDING;
  const isResolved = requestStatus === REQUEST_STATUS.APPROVED;
  const readOnly = isResolved;

  const isBackdated = useMemo(
    () =>
      cancellationRequestDate &&
      cancellationRequestDate.isBefore(dayjs(), 'day'),
    [cancellationRequestDate],
  );
  const amountExceedsPremium =
    grossPremium != null && +refundAmount > +grossPremium;

  const showAgentHint = (value) =>
    !!cancellationRequest && value != null && value !== '';

  // Label originally submitted by the agent (kept visible even after the field is edited).
  const submittedLabel = (options, value) => {
    const option = findOption(options, value);
    return option ? t(option.label) : '';
  };

  const translatedOptions = (options) =>
    options.map((option) => ({ ...option, label: t(option.label) }));

  const handleRecalculate = async () => {
    if (!cancellationRequestDate) {
      return;
    }
    // Set the field from the returned value directly; keying a useEffect on the refund
    // state would skip when the recalculated amount equals the previous one.
    const amount = await fetchRefundData(
      policyDetails,
      cancellationRequestDate.toDate(),
    );
    if (amount != null) {
      setRefundAmount(`${amount}`);
    }
  };

  const canCancel =
    !readOnly &&
    !!reason &&
    !!refundEligibility &&
    !!methodReceived &&
    !!cancellationRequestDate &&
    !amountExceedsPremium &&
    (!isBackdated || !!delayReason) &&
    (delayReason?.value !== PolicyCancellationDelayReasonValue.OTHER ||
      delayReasonOtherText.trim().length >= 3);

  const handleFinalizeCancellation = async () => {
    setShowConfirmCancel(false);
    try {
      const formattedDate = dayjs(cancellationRequestDate).format(
        'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
      );
      const result = await cancelPolicy(
        policyDetails,
        refundAmount,
        refundDate ?? formattedDate,
        reason.value,
        formattedDate,
        methodReceived.value,
        isBackdated ? delayReason.value : null,
        isBackdated &&
          delayReason?.value === PolicyCancellationDelayReasonValue.OTHER
          ? delayReasonOtherText
          : null,
        refundEligibility.value,
      );
      if (result) {
        navigate('/policy-cancellations');
      }
    } catch (error) {
      reportError(error);
    }
  };

  const handleDismiss = async () => {
    setShowConfirmDismiss(false);
    try {
      const result = await dismissPendingCancellation(policyId);
      if (result) {
        navigate('/policy-cancellations');
      }
    } catch (error) {
      reportError(error);
    }
  };

  const agentHint = (value, displayValue) =>
    showAgentHint(value) ? (
      <SoftTypography
        variant="caption"
        sx={{ color: 'info.main', display: 'block', mt: 0.5 }}
      >
        {t(`${path}.submittedByAgent`, { value: displayValue })}
      </SoftTypography>
    ) : null;

  const cancellationSummary = {
    policyNumber: policyId,
    requestDate: cancellationRequestDate
      ? dayjs(cancellationRequestDate).format('DD-MM-YYYY')
      : undefined,
    refundAmount: refundAmount !== '' ? refundAmount : undefined,
    reason: reason?.label ? t(reason.label) : undefined,
    methodReceived: methodReceived?.label ? t(methodReceived.label) : undefined,
    delayReason: delayReason?.label ? t(delayReason.label) : undefined,
    refundEligibility: refundEligibility?.label
      ? t(refundEligibility.label)
      : undefined,
  };

  const fieldLabel = (label, required = false) => (
    <SoftTypography
      variant="caption"
      fontWeight="bold"
      mb={0.5}
      display="block"
    >
      {t(label)}
      {required && <span aria-hidden="true"> *</span>}
    </SoftTypography>
  );

  return (
    <SoftBox data-testid="CancellationManagementTab">
      <SoftBox mb={3}>
        <SoftTypography variant="h5" fontWeight="bold">
          {t(`${path}.title`)}
        </SoftTypography>
        {requestStatus && (
          <SoftTypography variant="button" color="text">
            {t(`${path}.requestStatus`)}:{' '}
            <strong>{t(`${path}.STATUS.${requestStatus}`)}</strong>
          </SoftTypography>
        )}
      </SoftBox>

      <Grid container spacing={4}>
        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Box mb={3}>
            {fieldLabel(`${path}.cancellationRequestDate`)}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={cancellationRequestDate}
                disabled={readOnly}
                format="DD-MM-YYYY"
                maxDate={dayjs()}
                minDate={dayjs(policyDetails?.policy?.subscriptionDate)}
                sx={{ display: 'flex', maxWidth: '15rem' }}
                onChange={(date) => {
                  setCancellationRequestDate(date);
                }}
              />
            </LocalizationProvider>
            {agentHint(
              cancellationRequest?.requestDate,
              cancellationRequest?.requestDate
                ? dayjs(cancellationRequest.requestDate).format('DD-MM-YYYY')
                : '',
            )}
          </Box>

          <Box mb={3}>
            {fieldLabel(`${path}.policyPrice`)}
            <SoftInput value={grossPremium ?? ''} disabled />
          </Box>

          <Box mb={1}>
            {fieldLabel(`${path}.refundAmount`)}
            <SoftInput
              value={refundAmount}
              disabled={readOnly}
              error={amountExceedsPremium}
              onChange={(v) => {
                const value = v.target.value
                  .replace(/[^\d.]/g, '')
                  .replace(/(\..*)\./g, '$1')
                  .slice(0, 14);
                setRefundAmount(value);
              }}
            />
            {amountExceedsPremium && (
              <SoftTypography
                variant="caption"
                sx={{ color: 'red', display: 'block' }}
              >
                {t(`${path}.amountExceedsPremium`)}
              </SoftTypography>
            )}
            {!readOnly && (
              <SoftButton
                variant="text"
                color="info"
                size="small"
                disabled={!cancellationRequestDate}
                onClick={handleRecalculate}
                sx={{ pl: 0, mt: 0.5 }}
              >
                <Cached sx={{ mr: 0.5 }} fontSize="small" />
                {t(`${path}.recalculate`)}
              </SoftButton>
            )}
          </Box>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Box mb={3}>
            {fieldLabel(`${path}.methodReceivedLabel`)}
            <SoftSelect
              isDisabled={readOnly}
              placeholder={t(`${path}.methodReceivedLabel`)}
              options={translatedOptions(policyCancellationMethodReceived)}
              value={
                methodReceived
                  ? { ...methodReceived, label: t(methodReceived.label) }
                  : null
              }
              onChange={(option) => {
                setMethodReceived(option);
              }}
            />
            {agentHint(
              cancellationRequest?.methodReceived,
              submittedLabel(
                policyCancellationMethodReceived,
                cancellationRequest?.methodReceived,
              ),
            )}
          </Box>

          <Box mb={3}>
            {fieldLabel(`${path}.delayReasonLabel`)}
            <SoftSelect
              isDisabled={readOnly}
              placeholder={t(`${path}.delayReasonLabel`)}
              options={translatedOptions(policyCancellationDelayReasons)}
              value={
                delayReason
                  ? { ...delayReason, label: t(delayReason.label) }
                  : null
              }
              onChange={(option) => {
                setDelayReason(option);
              }}
            />
            {agentHint(
              cancellationRequest?.delayReason,
              submittedLabel(
                policyCancellationDelayReasons,
                cancellationRequest?.delayReason,
              ),
            )}
            {delayReason?.value ===
              PolicyCancellationDelayReasonValue.OTHER && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={delayReasonOtherText}
                  disabled={readOnly}
                  inputProps={{ maxLength: 50, minLength: 3 }}
                  error={delayReasonOtherText.trim().length < 3}
                  helperText={`${delayReasonOtherText.length}/50`}
                  onChange={(e) => {
                    setDelayReasonOtherText(e.target.value);
                  }}
                />
              </Box>
            )}
          </Box>

          <Box mb={3}>
            {fieldLabel(`${path}.cancellationReasonLabel`)}
            <SoftSelect
              isDisabled={readOnly}
              placeholder={t(`${path}.cancellationReasonLabel`)}
              options={translatedOptions(policyCancellationReasons)}
              value={reason ? { ...reason, label: t(reason.label) } : null}
              onChange={(option) => {
                setReason(option);
              }}
            />
            {agentHint(
              cancellationRequest?.reason,
              submittedLabel(
                policyCancellationReasons,
                cancellationRequest?.reason,
              ),
            )}
          </Box>

          <Box mb={1}>
            <RefundEligibilitySelect
              value={refundEligibility}
              onChange={(option) => {
                setRefundEligibility(option);
              }}
            />
            {agentHint(
              cancellationRequest?.refundEligibility,
              submittedLabel(
                policyCancellationRefundEligibility,
                cancellationRequest?.refundEligibility,
              ),
            )}
          </Box>
        </Grid>
      </Grid>

      {cancellationRequest && (
        <SoftBox mt={2} data-testid="CancellationAttachments">
          {fieldLabel(`${path}.attachmentsTitle`)}
          {cancellationRequest.attachments?.length ? (
            cancellationRequest.attachments.map((attachment) => (
              <SoftBox
                key={attachment.id}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <SoftButton
                  variant="text"
                  color="info"
                  size="small"
                  sx={{ pl: 0 }}
                  onClick={() => downloadAttachment(policyId, attachment.id)}
                >
                  <Download sx={{ mr: 0.5 }} fontSize="small" />
                  {attachment.fileName}
                </SoftButton>
                <SoftTypography variant="caption" color="text">
                  {formatFileSize(attachment.sizeBytes)}
                </SoftTypography>
              </SoftBox>
            ))
          ) : (
            <SoftTypography variant="button" color="text">
              {t(`${path}.noAttachments`)}
            </SoftTypography>
          )}
        </SoftBox>
      )}

      {isResolved && (
        <SoftBox mt={2}>
          <SoftTypography variant="button" color="text">
            {t(`${path}.resolvedNote`)}
          </SoftTypography>
        </SoftBox>
      )}

      {!readOnly && (
        <SoftBox mt={4} display="flex" gap={2}>
          <SoftButton
            variant="contained"
            color="info"
            disabled={!canCancel}
            onClick={() => setShowConfirmCancel(true)}
          >
            {t(`${path}.cancelPolicy`)}
          </SoftButton>
          {isPending && (
            <SoftButton
              variant="outlined"
              color="secondary"
              onClick={() => setShowConfirmDismiss(true)}
            >
              {t(`${path}.dismiss`)}
            </SoftButton>
          )}
        </SoftBox>
      )}

      <CancellationSummaryDialog
        open={showConfirmCancel}
        variant="cancel"
        summary={cancellationSummary}
        onClose={() => setShowConfirmCancel(false)}
        onConfirm={handleFinalizeCancellation}
      />
      <DialogConfirmation
        open={showConfirmDismiss}
        onClose={() => setShowConfirmDismiss(false)}
        onAction={handleDismiss}
        actionColor="secondary"
        actionLabel="confirm"
        path={`${path}.CONFIRM_DISMISS`}
      />
    </SoftBox>
  );
};

CancellationManagementTab.propTypes = {
  policyDetails: PropTypes.object,
};

export default CancellationManagementTab;
